import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import WebMediaPlayer from '@tunein/web-media-player';
import debug from '../../debugger';
import Player from '../models/Player';
import timeToPercentage from '../utils/timeToPercentage';
import playerName from '../constants/playerName';

const mediaErrorNames = [
  'MediaErrorAborted',
  'MediaErrorNetwork',
  'MediaErrorDecode',
  'MediaErrorSourceNotSupported',
];

function onError(callback, mediaElement) {
  return (event) => {
    const error = new Error(`${playerName.WebMediaPlayer} error`);
    const mediaError = get(mediaElement, 'error') || {};
    const { message, error: eventError, context } = event || {};

    if (!eventError.fatal) {
      return;
    }

    error.errorData = {
      ...eventError,
      context,

      // TODO: these are being used as a fail-safe for the experiment, but they might not be
      //  needed after we confirm that the above data is sufficient
      isMediaError: mediaErrorNames.includes(get(eventError, 'name')),
      eventMessage: message || get(error, 'message'),
      mediaErrorCode: mediaError.code,
      mediaErrorMessage: mediaError.message,
    };

    return callback(error);
  };
}

export default class WebMediaPlayerManager extends Player {
  #icyMetadataEnabled;

  #playerId;

  constructor(icyMetadataEnabled, playerId, keepMediaElementAlive = false) {
    super(playerName.WebMediaPlayer);

    this.keepMediaElementAlive = keepMediaElementAlive;
    this.#icyMetadataEnabled = icyMetadataEnabled;
    this.#playerId = playerId;

    this.mediaElement = document.createElement('audio');
    this.mediaElement.id = `web-tuner-audio-player-${this.#playerId}`;

    document.body.appendChild(this.mediaElement);
    this.mediaElement.setAttribute(
      'style',
      'width: 0px; height: 0px; display: none;',
    );
    this.player = new WebMediaPlayer({
      mediaElementId: this.mediaElement.id,
      keepMediaElementAlive: this.keepMediaElementAlive,
      onlyUseCoreEngine: true,
      debug: debug.enabled,
      enableIcyMetadata: this.#icyMetadataEnabled,
      // StreamPlayer handles timeout, so setting to an arbitrarily high value to circumvent WMP's internal timeout behavior
      timeoutMs: 100_000,
    });
  }

  on(event, callback) {
    this.player.on(event, callback);
  }

  off(event, callback) {
    this.player.off(event, callback);
  }

  /**
   * This is necessary on Safari mobile and desktop browsers due to autoplay restrictions.
   * See PlayerLoader.js for more details.
   * WMP is loaded asynchronously in init(), so blank mp3 should be loaded directly on the media element.
   * @param blankMp3Url {string}
   */
  async preload(blankMp3Url) {
    try {
      this.mediaElement.src = blankMp3Url;
      this.mediaElement.load();
      this.mediaElement.play();
    } catch (e) {
      // noop
    }
  }

  load(url, format, _position, _trackingUrl, useStreamMetadata) {
    return new Promise(async (resolve, reject) => {
      try {
        const onLoadError = (event) => {
          if (event?.error?.fatal) {
            this.player.off('error', onLoadError);
            onError(reject, this.player.getMediaElement())(event);
          }
        };

        this.player.on('error', onLoadError);

        await this.player.load({
          url,
          format,
          useStreamMetadata,
          position: _position,
        });

        if (this.player.isLoaded()) {
          resolve();
        }
      } catch (error) {
        onError(reject, this.player.getMediaElement())(error);
      }
    });
  }

  async play(playPosition) {
    await new Promise((resolve, reject) => {
      this.player.once('playing', resolve);

      const onPlayError = (event) => {
        if (event?.error?.fatal) {
          this.player.off('error', onPlayError);
        }

        onError(reject, this.player.getMediaElement())(event);
      };

      this.player.on('error', onPlayError);

      if (
        Number.isFinite(+playPosition) &&
        playPosition !== this.player.getCurrentTime()
      ) {
        this.player.once('canplay', () => {
          this.player.seekTo(playPosition);
        });
      }

      this.player.play();
    });
  }

  seek(position) {
    this.player.seekTo((position / 100) * this.player.getDuration());
  }

  pause() {
    this.player.pause();
  }

  stop() {
    this.player.stop();
  }

  canChangePlaybackRate() {
    return !!this.player.getPlaybackRate();
  }

  getPlaybackRate() {
    return this.player.getPlaybackRate();
  }

  changePlaybackRate(rate) {
    this.player.setPlaybackRate(rate);
  }

  unload() {
    this.player.unload();
  }

  // WMP doesn't provide a dedicated destroy method, but its unload method unloads and destroys
  destroy() {
    this.unload();
  }

  setVolume(volume) {
    if (isNumber(volume)) {
      this.player.setVolume(volume / 100);
    }
  }

  getVolume() {
    return this.player.getVolume() || 0;
  }

  toggleMute() {
    this.player.toggleMute();
  }

  getTimes() {
    const duration = this.player.getDuration() || 0;
    const elapsedSeconds = this.player.getCurrentTime() || 0;

    return {
      duration: Math.floor(duration),
      elapsedSeconds: Math.floor(elapsedSeconds),
      elapsedPercent: timeToPercentage(elapsedSeconds, duration),
    };
  }
}
